"use client";

import { useEffect, useState, type ReactNode } from "react";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { useAuth } from "@/hooks/use-auth";
import { PaymentSourceType, SubscriptionPlanId } from "@/features/subscription/types";
import type { UserSubscription } from "@/features/subscription/types";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { FEATURES } from "@/config/experiments";
import { useSubscription } from "@/features/subscription/queries";
import { useMediaList } from "@/features/media/queries";
import { useTotalChatUsage } from "@/features/dashboard/queries";
import { isAnalyticsConsentGranted } from "@/lib/integrations/ga4";
import { resolveGrowthBookClientId } from "@/lib/integrations/growthbook-cookie";
import { postAssignment, type AssignmentCohortSnapshot, type BillingRail } from "@/features/experiments/api";
import { STORAGE_KEYS } from "@/config/constants";

// Resolved once per page load. Matches WP plugin getOrCreateStableId priority:
// _ga → gb_id → fresh. SSR-safe; null on SSR and when cookies are blocked, so
// anonymous bucketing falls through to defaultValue (= control) — no flicker.
const { id: gbClientId, source: gbClientIdSource } = resolveGrowthBookClientId();

let _setRateFired = false;

const _assignmentDedupe = new Set<string>();

type CohortInputs = {
  user: ReturnType<typeof useAuth>["user"];
  subscription: UserSubscription | undefined;
  mediaList: { totalCount?: number } | undefined;
  chatUsage: { totalPrompts?: number } | undefined;
};

const _cohortInputs: CohortInputs = {
  user: null,
  subscription: undefined,
  mediaList: undefined,
  chatUsage: undefined,
};

function deriveBillingRail(subscription: UserSubscription | undefined): BillingRail | null {
  if (subscription === undefined) return null;
  switch (subscription.paymentSource) {
    case PaymentSourceType.Stripe: return "stripe";
    case PaymentSourceType.Paddle: return "paddle";
    case PaymentSourceType.Revenuecat: return "revenuecat";
    case PaymentSourceType.Direct_Deposit: return "direct-deposit";
    default: return "none";
  }
}

function buildCohortSnapshot(inputs: CohortInputs): AssignmentCohortSnapshot {
  const { user, subscription, mediaList, chatUsage } = inputs;
  const isPaidSubscriber = subscription === undefined
    ? null
    : !!(
      subscription
      && !subscription.isExpired
      && subscription.planId !== SubscriptionPlanId.Speak_Free_Trial
      && subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go
    );
  return {
    planId: user?.subscription?.planId ?? subscription?.planId ?? null,
    isTrial: user
      ? (user.subscription?.planId === SubscriptionPlanId.Speak_Free_Trial)
      : null,
    isCompanyDomain: user?.isCompanyDomain ?? null,
    isPaidSubscriber,
    uploadVolume: mediaList?.totalCount ?? null,
    magicPromptQueries: chatUsage?.totalPrompts ?? null,
    billingRail: deriveBillingRail(subscription),
    revenueTier: null,
  };
}

function readLocalStorage(key: string): string | null {
  try {
    return typeof window !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function createGrowthBook(): GrowthBook {
  const instance = new GrowthBook({
    enableDevMode: import.meta.env.DEV,
    trackingCallback: (experiment, result) => {
      const variationName = (
        typeof result.value === "string" ||
          typeof result.value === "number" ||
          typeof result.value === "boolean"
          ? String(result.value)
          : (JSON.stringify(result.value) ?? "undefined")
      ).slice(0, 80);

      // Sentry breadcrumb only (no captureEvent); the countable exposure record is postAssignment below.
      trackFunnel("activation", "experiment_assigned", {
        experiment_id: experiment.key.slice(0, 80),
        variant_id: String(result.variationId),
        variation_name: variationName,
        in_experiment: result.inExperiment,
        hash_attribute: result.hashAttribute?.slice(0, 80) ?? null, // pseudonymous: user.companyId (server-issued unified tracking key), never email/PII
        gb_client_id: gbClientId?.slice(0, 80) ?? null,
      });

      // dataLayer push gated on result.inExperiment to mirror WP plugin parity.
      if (result.inExperiment && isAnalyticsConsentGranted()) {
        window.dataLayer?.push({
          event: "experiment_viewed",
          experiment_id: experiment.key.slice(0, 80),
          variation_id: String(result.variationId),
          variation_name: variationName,
          in_experiment: true,
          hash_attribute: result.hashAttribute?.slice(0, 80), // intentionally undefined (not null) — GTM omits absent keys; do NOT change to ?? null (Sentry path uses null to keep it queryable)
          gb_client_id: gbClientId?.slice(0, 80), // intentionally undefined-when-null so GTM omits the key
        });
      }

      const dedupeKey = `${experiment.key}:${result.variationId}`;
      if (_assignmentDedupe.has(dedupeKey)) return;
      _assignmentDedupe.add(dedupeKey);
      void postAssignment({
        experimentKey: experiment.key,
        variantId: String(result.variationId),
        variantName: variationName,
        inExperiment: result.inExperiment,
        hashAttribute: result.hashAttribute ?? null,
        hashValue: result.hashValue ?? null,
        gbClientId: gbClientId ?? null,
        userId: _cohortInputs.user?._id ?? null,
        pageLocation: typeof window !== "undefined" ? window.location.href : null,
        pageReferrer: typeof document !== "undefined" ? document.referrer : null,
        // STORAGE_KEYS naming is inverted relative to contents (see utm.ts): the LANDING_PAGE
        // key holds the external referrer, and the REFERRER key holds the first in-app URL.
        originalLandingPage: readLocalStorage(STORAGE_KEYS.GA_ORIGINAL_LANDING_PAGE),
        originalReferrer: readLocalStorage(STORAGE_KEYS.GA_ORIGINAL_REFERRER),
        sessionId: null,
        source: "client",
        cohortSnapshot: buildCohortSnapshot(_cohortInputs),
      });
    },
  });
  // InitSyncOptions.payload wraps the FeatureApiResponse shape — features nested under payload.
  instance.initSync({ payload: { features: FEATURES } });

  // Anonymous bucketing: expose gb_client_id as a separate attribute so pricing
  // experiments can declare hashAttribute: "gb_client_id" per-rule. `id` stays
  // user.companyId-bound (set in the useEffect below) to protect sticky bucketing
  // of the 11 existing experiments. gbClientId is null on SSR and when cookies are
  // blocked — in both cases we skip setAttributes so pricing experiments fall
  // through to defaultValue (= control).
  if (gbClientId) {
    instance.setAttributes({ gb_client_id: gbClientId });
  }

  return instance;
}

/**
 * Anonymous experiment provider for public routes mounted OUTSIDE RootProviders
 * (e.g. /claim-offer). Provides a GrowthBookProvider so GrowthBook hooks resolve,
 * but has no AuthProvider/useAuth dependency — so logged-out visitors aren't
 * bounced to /auth/login. Buckets on the anonymous gb_client_id attribute already
 * set inside createGrowthBook(); no user-bound attributes are applied.
 */
export function AnonymousExperimentProvider({ children }: { children: ReactNode }) {
  const [gb] = useState(createGrowthBook);
  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
}

export function ExperimentProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [gb] = useState(createGrowthBook);
  const { data: subscription } = useSubscription({ enabled: !!user });
  const { data: mediaList } = useMediaList({ pageSize: 1, enabled: !!user });
  const { data: chatUsage } = useTotalChatUsage(!!user);

  // Fire set-rate instrumentation exactly once per mount. Sentry path is ungated
  // (operational); dataLayer path is consent-gated (analytics).
  useEffect(() => {
    if (_setRateFired || gbClientIdSource === "ssr") return;
    _setRateFired = true;
    trackFunnel("activation", "gb_id_resolved", {
      source: gbClientIdSource,
      gb_client_id: gbClientId?.slice(0, 80) ?? null,
    });
    if (isAnalyticsConsentGranted()) {
      window.dataLayer?.push({
        event: "gb_id_resolved",
        gb_id_source: gbClientIdSource,
        gb_client_id: gbClientId?.slice(0, 80),
      });
    }
  }, []);

  useEffect(() => {
    _cohortInputs.user = user;
    _cohortInputs.subscription = subscription;
    _cohortInputs.mediaList = mediaList ?? undefined;
    _cohortInputs.chatUsage = chatUsage ?? undefined;
  }, [user, subscription, mediaList, chatUsage]);

  useEffect(() => {
    if (!user) return;
    if (!user.companyId) return; // defer until server-issued companyId resolves; never fall back to _id
    gb.setAttributes({
      id: user.companyId, // server-issued unified tracking key — pseudonymous, not email/PII
      gb_client_id: gbClientId ?? undefined, // preserve cross-domain attribute on login
      planId: user.subscription?.planId ?? "none",
      isTrial: user.subscription?.planId === SubscriptionPlanId.Speak_Free_Trial,
      userType: user.userType ?? "",
      isCompanyDomain: user.isCompanyDomain ?? false,
      salesQualified: user.salesQualified ?? false,
      isNewUser: user.isNewUser ?? false,
      onboardingGoal: user.onboarding?.goal ?? "",
      teamSize: user.onboarding?.teamSize ?? "",
      apolloEnriched: !!user.apollo?.enrichedAt,
      ...(mediaList != null ? {
        hasUploaded: mediaList.totalCount > 0,
        uploadVolume: mediaList.totalCount ?? 0, // ICP-2: total media files uploaded; omit while loading
      } : {}),
      // != null catches both undefined (query loading) and null (no-subscription server body) — omit the attribute in both cases; do not tighten to !== undefined (null body throws on .remainingDays).
      ...(subscription != null ? { trialDaysRemaining: subscription.remainingDays } : {}),
      ...(subscription != null ? {
        isPaidSubscriber: !!(
          subscription &&
          !subscription.isExpired &&
          subscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
          subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go
        ),
      } : {}),
      ...(chatUsage != null ? { magicPromptQueries: chatUsage.totalPrompts } : {}),
    });

    // Mirror the onboarding-auto-setup variant into attributes so other experiments
    // (e.g. trial-first-upload-prompt) can carve out treatment users via targeting.
    const autoSetupVariant = gb.getFeatureValue("onboarding-auto-setup", "control");
    gb.setAttributes({
      ...gb.getAttributes(),
      "onboarding-auto-setup": autoSetupVariant,
    });
  }, [user, subscription, mediaList, chatUsage]);

  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
}
